import { useMemo, useRef, useState } from "react";
import {
  Bed,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Lamp,
  LibraryBig,
  Loader2,
  Pencil,
  Sofa,
} from "lucide-react";
import { toast } from "sonner";
import {
  useProtocolInventory,
  useUpdateInventoryItem,
} from "@/hooks/useProtocol";
import {
  PROTOCOL_STORAGE_BUCKET,
  type ItemCondition,
  type ProtocolInventoryItem,
  type Severity,
} from "@/lib/protocol";
import { supabase } from "@/lib/supabase";
import type { ProtocolCopy } from "@/lib/protocol-copy";

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  bed: Bed,
  desk: Pencil,
  chair: Sofa,
  shelf: LibraryBig,
  lamp: Lamp,
};

export function InventoryStep({
  protocolId,
  t,
}: {
  protocolId: string;
  t: ProtocolCopy;
}) {
  const { data: items = [], isLoading } = useProtocolInventory(protocolId);
  const update = useUpdateInventoryItem(protocolId);
  const sorted = useMemo(
    () => [...items].sort((a, b) => a.ordering - b.ordering),
    [items]
  );

  return (
    <div>
      <h2 className="text-[26px] font-bold leading-tight text-neutral-900 md:text-3xl">
        {t.inventoryTitle}
      </h2>
      <p className="mt-1 text-sm text-neutral-500 md:max-w-2xl">
        {t.inventorySubtitle}
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-3 md:mt-8 md:grid-cols-2 md:gap-4 lg:grid-cols-3">
          {sorted.map((item) => (
            <InventoryRow
              key={item.id}
              item={item}
              t={t}
              onChange={(patch) => update.mutateAsync({ id: item.id, patch })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function isInventoryComplete(items: ProtocolInventoryItem[]): boolean {
  if (!items.length) return false;
  return items.every(
    (it) =>
      !!it.condition &&
      !!it.photo_storage_path &&
      (it.condition !== "damaged" ||
        (it.defect_description?.trim().length ?? 0) > 0)
  );
}

function InventoryRow({
  item,
  t,
  onChange,
}: {
  item: ProtocolInventoryItem;
  t: ProtocolCopy;
  onChange: (patch: Partial<ProtocolInventoryItem>) => Promise<void>;
}) {
  const Icon = ICONS[item.icon ?? ""] ?? Bed;
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const dotColor =
    item.condition === "good"
      ? "bg-emerald-500"
      : item.condition === "wear"
        ? "bg-amber-500"
        : item.condition === "damaged"
          ? "bg-rose-500"
          : "bg-neutral-300";

  // An item is fully complete when BOTH a photo AND a condition are set,
  // and damaged items have a defect description.
  const isComplete =
    !!item.condition &&
    !!item.photo_storage_path &&
    (item.condition !== "damaged" ||
      (item.defect_description?.trim().length ?? 0) > 0);

  const conditionLabel = (c: ItemCondition | null) => {
    if (c === "good") return t.conditionGood;
    if (c === "wear") return t.conditionWear;
    if (c === "damaged") return t.conditionDamaged;
    return t.conditionChoose;
  };

  const setCondition = async (c: ItemCondition) => {
    setBusy(true);
    try {
      await onChange({ condition: c });
    } finally {
      setBusy(false);
    }
  };

  const onPickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${item.protocol_id}/inventory/${item.id}-${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(PROTOCOL_STORAGE_BUCKET)
        .upload(path, file, {
          upsert: false,
          contentType: file.type || "image/jpeg",
        });
      if (upErr) throw upErr;
      await onChange({ photo_storage_path: path });
    } catch (err: any) {
      toast.error(err?.message ?? t.photoUploadFailed);
    } finally {
      setBusy(false);
    }
  };

  const severityLabel = (s: Severity) =>
    s === "low"
      ? t.severityLow
      : s === "medium"
        ? t.severityMedium
        : t.severityHigh;

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 p-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-100 text-neutral-700">
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <div className="text-[15px] font-semibold text-neutral-900">
            {item.label}
          </div>
          {item.meta ? (
            <div className="text-sm text-neutral-500">{item.meta}</div>
          ) : null}
        </div>
        {isComplete ? (
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500 text-white">
            <CheckCircle2 className="h-5 w-5" />
          </span>
        ) : item.photo_storage_path ? (
          // Photo uploaded but condition still missing — keep neutral so the
          // user knows the row isn't done yet.
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-600"
            title={t.conditionLabel}
          >
            <Camera className="h-5 w-5" />
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-500" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-neutral-100 text-neutral-700"
          >
            <Camera className="h-5 w-5" />
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-rose-500" />
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPickPhoto}
        />
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between border-t border-neutral-100 px-4 py-2.5 text-sm"
      >
        <span className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} />
          <span className="text-neutral-600">
            {t.conditionLabel}:{" "}
            <span className="font-semibold text-neutral-900">
              {conditionLabel(item.condition)}
            </span>
          </span>
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-neutral-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-neutral-400" />
        )}
      </button>

      {expanded ? (
        <div className="space-y-3 px-4 pb-4 pt-1">
          <div className="flex flex-wrap gap-2">
            {(["good", "wear", "damaged"] as ItemCondition[]).map((c) => (
              <button
                key={c}
                type="button"
                disabled={busy}
                onClick={() => setCondition(c)}
                className={[
                  "rounded-full px-3 py-1.5 text-xs font-medium transition",
                  item.condition === c
                    ? c === "good"
                      ? "bg-emerald-500 text-white"
                      : c === "wear"
                        ? "bg-amber-500 text-white"
                        : "bg-rose-500 text-white"
                    : "bg-neutral-100 text-neutral-700",
                ].join(" ")}
              >
                {conditionLabel(c)}
              </button>
            ))}
          </div>

          {item.condition === "damaged" ? (
            <div className="space-y-2 rounded-xl border border-rose-100 bg-rose-50/60 p-3">
              <textarea
                placeholder={t.defectDescription}
                value={item.defect_description ?? ""}
                onChange={(e) =>
                  onChange({ defect_description: e.target.value })
                }
                rows={2}
                className="w-full resize-none rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-rose-400"
              />
              <div className="flex gap-2">
                {(["low", "medium", "high"] as Severity[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onChange({ defect_severity: s })}
                    className={[
                      "flex-1 rounded-full px-2 py-1 text-xs font-medium transition",
                      item.defect_severity === s
                        ? "bg-neutral-900 text-white"
                        : "bg-white text-neutral-700 ring-1 ring-neutral-200",
                    ].join(" ")}
                  >
                    {severityLabel(s)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
